import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useDropzone } from "react-dropzone";
import { AtlassianConnector } from "@intelligenic/chat-components";
import { getUserAnswersWithQuestions, updateUserAnswer, updateProjectApi, createFeatureApi, markWorkProductsNeedingRebuild } from "@/api/api";
import { getAtlassianInfo, syncAtlassianData, AtlassianInfoResponse } from "@/api/integrations/atlassian";
import { checkAzureDevOpsCredentials, AzureDevOpsCredentialsCheckResponse } from "@/api/integrations/azureDevOps";
import fetchWithAuth from "@/api/secureFetch";
import { getWorkProcessorUrl } from "@/api/workProcessor";
import {
    UploadQueueList,
    useSequentialFileUploadQueue,
    type SequentialFileUploadQueueLifecycleHandlers
} from "@/components/fileUploadQueue";
import useFileUpload, { type FileExtractionEvent } from "@/hooks/useFileUpload";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    LinearProgress,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    alpha,
    Backdrop,
    List,
    ListItem,
    ListItemText,
    Divider as MuiDivider
} from "@mui/material";
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    CloudUpload as CloudUploadIcon,
    AttachFile as AttachFileIcon,
    Sync as SyncIcon
} from "@mui/icons-material";
import { useAppSelector } from "@/state/hooks";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";
const AGENT_URL = import.meta.env.VITE_AGENT_URL || "";

// Base step definitions for the stepper
const BASE_STEPS = [
    { label: "Info", key: "info" },
    { label: "Details", key: "details" },
    { label: "Files", key: "files" },
    { label: "Integration Sync", key: "integration" },
    { label: "Change Impact", key: "impact" }
];

// Product type options
const productTypes = [
    { value: "NewProduct", label: "New Product" },
    { value: "NewFeature", label: "New Feature" },
    { value: "Existing", label: "Existing" }
];

// Product data interface
export interface ProductData {
    productId: number;
    type: string;
    name: string;
    description: string;
    users: string;
    problem: string;
    solution: string;
    files: File[];
    jiraProject: string;
    githubProject: string;
}

// Change impact data interface
export interface ChangeImpactData {
    featuresChanged: number;
    workProductsOutOfDate: number;
    severity: "Warning" | "Critical" | "Info";
    sourceOfChange: string;
    changeSummary: string;
    timestamp: string;
    version: string;
    impactedFeatures: string[];
    impactedUserStories: string[];
    impactedWorkProducts: string[];
}

// Feature hierarchy data interface (from API)
interface UserStory {
    title: string;
    story_number: string;
    user_story_id: string;
    external_story_number: string | null;
}

interface Feature {
    title: string;
    featureid: number;
    user_stories: UserStory[];
}

interface FeatureHierarchyResponse {
    features: Feature[];
    project_id: number;
}

interface UpdateProductModalProps {
    open: boolean;
    onClose: () => void;
    productId: number;
    onUpdate?: (data: ProductData) => Promise<void>;
    initialData?: Partial<ProductData>;
    changeImpact?: ChangeImpactData;
}

// Question IDs for workproduct_user_answers mapping
const QUESTION_IDS = {
    TYPE: 20,
    USERS: 22,
    PROBLEM: 23,
    SOLUTION: 24
} as const;

// Interface to track answer IDs for updates
interface AnswerIds {
    type?: number;
    users?: number;
    problem?: number;
    solution?: number;
}

// Custom Step Icon Component
const StepIcon: React.FC<{
    active: boolean;
    completed: boolean;
    stepNumber: number;
}> = ({ active, completed, stepNumber }) => {
    if (completed) {
        return (
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "#103699",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: active ? "#103699" : "#E0E0E0",
                color: active ? "white" : "#707070",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600
            }}
        >
            {stepNumber}
        </Box>
    );
};

export const UpdateProductModal: React.FC<UpdateProductModalProps> = ({ open, onClose, productId, onUpdate, initialData, changeImpact }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { getToken } = useAuth();
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);

    // Step state
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

    // Form data state
    const [formData, setFormData] = useState<ProductData>({
        productId: productId,
        type: initialData?.type || "",
        name: initialData?.name || "",
        description: initialData?.description || "",
        users: initialData?.users || "",
        problem: initialData?.problem || "",
        solution: initialData?.solution || "",
        files: initialData?.files || [],
        jiraProject: initialData?.jiraProject || "",
        githubProject: initialData?.githubProject || ""
    });

    // Loading states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Track answer IDs for updating existing answers
    const [answerIds, setAnswerIds] = useState<AnswerIds>({});

    // File upload state
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFileCount, setUploadedFileCount] = useState(0);
    const manualInputRef = useRef<HTMLInputElement | null>(null);
    const lifecycleHandlersRef = useRef<SequentialFileUploadQueueLifecycleHandlers | null>(null);

    // Jira connection state
    const [jiraConnected, setJiraConnected] = useState(false);
    const [jiraConnectionInfo, setJiraConnectionInfo] = useState<{
        cloudId: string;
        domain: string;
        projectId: string;
        projectName: string;
    } | null>(null);

    // Atlassian integration info (from getAtlassianInfo API)
    const [atlassianInfo, setAtlassianInfo] = useState<AtlassianInfoResponse | null>(null);
    const [isLoadingAtlassianInfo, setIsLoadingAtlassianInfo] = useState(false);

    // Azure DevOps connection state
    const [azureDevOpsConnected, setAzureDevOpsConnected] = useState(false);
    const [azureDevOpsConnectionInfo, setAzureDevOpsConnectionInfo] = useState<{
        organization: string;
        projectId: string;
        projectName: string;
    } | null>(null);
    const [azureDevOpsInfo, setAzureDevOpsInfo] = useState<AzureDevOpsCredentialsCheckResponse | null>(null);
    const [isLoadingAzureDevOpsInfo, setIsLoadingAzureDevOpsInfo] = useState(false);

    // PM Tool type from external_link_type (JIRA or AZURE_DEVOPS)
    const [pmToolType, setPmToolType] = useState<string | null>(null);

    // Jira sync state
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

    // Project stats state
    const [projectStats, setProjectStats] = useState<{ product_count: number; feature_count: number; user_story_count: number } | null>(null);

    // Detailed project stats state (for impact step)
    const [detailedStats, setDetailedStats] = useState<{ projectname: string | null; workproduct_list: string | null; features_list: string | null; user_story_list: string | null } | null>(null);

    // Feature hierarchy state (for impact step)
    const [featureHierarchy, setFeatureHierarchy] = useState<FeatureHierarchyResponse | null>(null);

    // Track if we've already fetched data for this modal open to prevent duplicate calls
    const hasFetchedRef = useRef(false);

    // Quick-compare progress state
    const [rebuildStatus, setRebuildStatus] = useState<string[]>([]);
    const [quickComparePhase, setQuickComparePhase] = useState<string>("");

    // Comparison result state
    const [comparisonResult, setComparisonResult] = useState<any>(null);
    const [showDrasticChangeDialog, setShowDrasticChangeDialog] = useState(false);
    const [showMinorEvolutionDialog, setShowMinorEvolutionDialog] = useState(false);
    const [showProductTooLargeDialog, setShowProductTooLargeDialog] = useState(false);
    const [showNoChangesDialog, setShowNoChangesDialog] = useState(false);
    const [showRefinementOnlyDialog, setShowRefinementOnlyDialog] = useState(false);

    // Saving state for showing progress during YES actions
    const [isSaving, setIsSaving] = useState(false);

    // New product name for CREATE_NEW_PRODUCT flow
    const [newProductName, setNewProductName] = useState("");

    // Build job state for CREATE_NEW_PRODUCT flow
    const [isBuildingProduct, setIsBuildingProduct] = useState(false);
    const [buildJobProgress, setBuildJobProgress] = useState(0);
    const [buildJobMessage, setBuildJobMessage] = useState("");
    const [buildJobSectionsCompleted, setBuildJobSectionsCompleted] = useState(0);
    const [buildJobTotalSections, setBuildJobTotalSections] = useState(0);
    const [buildJobNewProjectId, setBuildJobNewProjectId] = useState<number | null>(null);
    const [buildJobError, setBuildJobError] = useState<string | null>(null);
    const buildJobPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Store original answers for rollback if user cancels update
    const [originalAnswers, setOriginalAnswers] = useState<Array<{ questionid: number; answer: string }>>([]);

    // Store original description for rollback if user cancels update
    const [originalDescription, setOriginalDescription] = useState<string>("");

    // Dynamically compute visible steps based on PM tool link status
    const steps = useMemo(() => {
        // Show Integration Sync step if any PM tool is linked (Jira or Azure DevOps)
        const hasLinkedPmTool = pmToolType === "JIRA" || pmToolType === "AZURE_DEVOPS" || atlassianInfo?.status === "LINKED" || azureDevOpsConnected;
        if (hasLinkedPmTool) {
            return BASE_STEPS;
        }
        // Filter out the integration step if no PM tool is linked
        return BASE_STEPS.filter(step => step.key !== "integration");
    }, [atlassianInfo?.status, pmToolType, azureDevOpsConnected]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            hasFetchedRef.current = false;
            setShowNoChangesDialog(false);
            setShowRefinementOnlyDialog(false);
            setQuickComparePhase("");
            setComparisonResult(null);
            setRebuildStatus([]);
            setNewProductName("");
            setOriginalDescription("");
            // Clean up build job polling
            if (buildJobPollingRef.current) {
                clearTimeout(buildJobPollingRef.current);
                buildJobPollingRef.current = null;
            }
            setIsBuildingProduct(false);
            setBuildJobProgress(0);
            setBuildJobMessage("");
            setBuildJobSectionsCompleted(0);
            setBuildJobTotalSections(0);
            setBuildJobNewProjectId(null);
            setBuildJobError(null);
            // Reset formData when modal closes
            setFormData({
                productId: productId,
                type: initialData?.type || "",
                name: initialData?.name || "",
                description: initialData?.description || "",
                users: initialData?.users || "",
                problem: initialData?.problem || "",
                solution: initialData?.solution || "",
                files: initialData?.files || [],
                jiraProject: initialData?.jiraProject || "",
                githubProject: initialData?.githubProject || ""
            });
            setActiveStep(0);
            setCompletedSteps(new Set());
        }
    }, [open, productId, initialData]);

    // Clean up build job polling on unmount
    useEffect(() => {
        return () => {
            if (buildJobPollingRef.current) {
                clearTimeout(buildJobPollingRef.current);
                buildJobPollingRef.current = null;
            }
        };
    }, []);

    // Fetch product data when modal opens
    useEffect(() => {
        const fetchProductData = async () => {
            if (!open || !productId || hasFetchedRef.current) return;

            // Mark as fetched immediately to prevent duplicate calls
            hasFetchedRef.current = true;

            // Check if we're adding a new feature (vs editing an existing product)
            const isAddingNewFeature = initialData?.type === "NewFeature";

            // Reset formData and state before fetching new product data
            setFormData({
                productId: productId,
                type: initialData?.type || "",
                name: initialData?.name || "",
                description: initialData?.description || "",
                users: initialData?.users || "",
                problem: initialData?.problem || "",
                solution: initialData?.solution || "",
                files: initialData?.files || [],
                jiraProject: initialData?.jiraProject || "",
                githubProject: initialData?.githubProject || ""
            });
            setAnswerIds({});
            setActiveStep(0);
            setCompletedSteps(new Set());

            setIsLoadingData(true);

            try {
                const token = await getToken();
                if (!token) {
                    console.error("No auth token available");
                    hasFetchedRef.current = false; // Reset on error to allow retry
                    return;
                }

                // Declare variables at function scope
                let externalLinkType: string | null = null;
                let externalLink: any = null;

                // Fetch project info
                const projectResponse = await fetchWithAuth(`/api/project/${productId}`, token, {
                    method: "GET"
                });
                if (projectResponse.ok) {
                    const project = await projectResponse.json();

                    // Capture the external_link_type to determine which PM tool to show
                    externalLinkType = project.external_link_type || null;
                    externalLink = project.external_link || null;
                    setPmToolType(externalLinkType);

                    // If NOT adding a new feature, populate name and description from project
                    if (!isAddingNewFeature && project?.project_name) {
                        setFormData(prev => ({
                            ...prev,
                            name: project.project_name,
                            description: project.project_description || ""
                        }));
                        // Snapshot the original description for rollback on cancel
                        setOriginalDescription(project.project_description || "");
                    }
                }

                // If NOT adding a new feature, fetch existing product answers
                if (!isAddingNewFeature) {
                    const answers = await getUserAnswersWithQuestions(productId, token);
                    if (answers && answers.length > 0) {
                        const newAnswerIds: AnswerIds = {};
                        const updates: Partial<ProductData> = {};
                        const origAnswers: Array<{ questionid: number; answer: string }> = [];

                        answers.forEach(answer => {
                            // Store original answer for potential rollback (includes team makeup - questionid 25)
                            const relevantQuestionIds = [QUESTION_IDS.TYPE, QUESTION_IDS.USERS, QUESTION_IDS.PROBLEM, QUESTION_IDS.SOLUTION, 25] as number[];
                            if (relevantQuestionIds.includes(answer.questionid)) {
                                origAnswers.push({
                                    questionid: answer.questionid,
                                    answer: answer.answer || ""
                                });
                            }

                            switch (answer.questionid) {
                                case QUESTION_IDS.TYPE:
                                    updates.type = answer.answer || "";
                                    newAnswerIds.type = answer.answer_id;
                                    break;
                                case QUESTION_IDS.USERS:
                                    updates.users = answer.answer || "";
                                    newAnswerIds.users = answer.answer_id;
                                    break;
                                case QUESTION_IDS.PROBLEM:
                                    updates.problem = answer.answer || "";
                                    newAnswerIds.problem = answer.answer_id;
                                    break;
                                case QUESTION_IDS.SOLUTION:
                                    updates.solution = answer.answer || "";
                                    newAnswerIds.solution = answer.answer_id;
                                    break;
                            }
                        });

                        setAnswerIds(newAnswerIds);
                        setFormData(prev => ({ ...prev, ...updates }));
                        setOriginalAnswers(origAnswers);
                    }
                }

                // Fetch file count for this product
                const filesResponse = await fetchWithAuth(`${BACKEND_URI}/api/projects/${productId}/vector-files`, token, { method: "GET" });
                if (filesResponse.ok) {
                    const filesData = await filesResponse.json();
                    setUploadedFileCount(filesData.relations?.length || 0);
                }

                // Fetch project stats (feature and user story counts)
                const statsResponse = await fetchWithAuth(`${BACKEND_URI}/api/project/${productId}/stats`, token, { method: "GET" });
                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setProjectStats(statsData);
                }

                // Fetch detailed project stats (for impact step lists)
                const detailedStatsResponse = await fetchWithAuth(`${BACKEND_URI}/api/project/${productId}/detailed-stats`, token, { method: "GET" });
                if (detailedStatsResponse.ok) {
                    const detailedStatsData = await detailedStatsResponse.json();
                    setDetailedStats(detailedStatsData);
                }

                // Fetch feature hierarchy (for impact step)
                const hierarchyResponse = await fetchWithAuth(`${BACKEND_URI}/api/project/${productId}/feature-hierarchy`, token, { method: "GET" });
                if (hierarchyResponse.ok) {
                    const hierarchyData = await hierarchyResponse.json();
                    setFeatureHierarchy(hierarchyData);
                }

                // Fetch PM tool integration info based on external_link_type
                if (userId) {
                    if (externalLinkType === "AZURE_DEVOPS") {
                        // Fetch Azure DevOps integration info
                        setIsLoadingAzureDevOpsInfo(true);
                        try {
                            const azureResponse = await checkAzureDevOpsCredentials(userId, token);
                            setAzureDevOpsInfo(azureResponse);

                            // If connected, pre-populate the Azure DevOps connection info
                            if (azureResponse.hasCredentials && azureResponse.isConnected) {
                                setAzureDevOpsConnected(true);
                                // Use metadata from credential or from external_link
                                const adoMetadata = azureResponse.metadata || {};
                                const parsedExternalLink = typeof externalLink === "string" ? JSON.parse(externalLink) : externalLink;
                                setAzureDevOpsConnectionInfo({
                                    organization: adoMetadata.organization || parsedExternalLink?.organization || "",
                                    projectId: adoMetadata.projectId || parsedExternalLink?.projectId || "",
                                    projectName: adoMetadata.projectName || parsedExternalLink?.projectName || ""
                                });
                            }
                        } catch (azureError) {
                            console.error("Error fetching Azure DevOps info:", azureError);
                        } finally {
                            setIsLoadingAzureDevOpsInfo(false);
                        }
                    } else if (externalLinkType === "JIRA") {
                        // Fetch Atlassian/Jira integration info
                        setIsLoadingAtlassianInfo(true);
                        try {
                            const atlassianResponse = await getAtlassianInfo({ userId, projectId: productId }, token);
                            setAtlassianInfo(atlassianResponse);

                            // If already linked, pre-populate the Jira connection info
                            if (atlassianResponse.status === "LINKED" && atlassianResponse.atlassian) {
                                setJiraConnected(true);
                                setJiraConnectionInfo({
                                    cloudId: atlassianResponse.atlassian.cloudId,
                                    domain: atlassianResponse.atlassian.domain,
                                    projectId: atlassianResponse.atlassian.projectId,
                                    projectName: atlassianResponse.atlassian.projectName
                                });
                                setFormData(prev => ({ ...prev, jiraProject: atlassianResponse.atlassian!.projectName }));
                            }
                        } catch (atlassianError) {
                            console.error("Error fetching Atlassian info:", atlassianError);
                        } finally {
                            setIsLoadingAtlassianInfo(false);
                        }
                    } else {
                        // No PM tool linked - check if user has any integration available
                        setIsLoadingAtlassianInfo(true);
                        try {
                            const atlassianResponse = await getAtlassianInfo({ userId, projectId: productId }, token);
                            setAtlassianInfo(atlassianResponse);

                            if (atlassianResponse.status === "LINKED" && atlassianResponse.atlassian) {
                                setJiraConnected(true);
                                setJiraConnectionInfo({
                                    cloudId: atlassianResponse.atlassian.cloudId,
                                    domain: atlassianResponse.atlassian.domain,
                                    projectId: atlassianResponse.atlassian.projectId,
                                    projectName: atlassianResponse.atlassian.projectName
                                });
                                setFormData(prev => ({ ...prev, jiraProject: atlassianResponse.atlassian!.projectName }));
                            }
                        } catch (atlassianError) {
                            console.error("Error fetching Atlassian info:", atlassianError);
                        } finally {
                            setIsLoadingAtlassianInfo(false);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching product data:", error);
                hasFetchedRef.current = false; // Reset on error to allow retry
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchProductData();
    }, [open, productId, getToken, userId]);

    // Handle step navigation
    const handleNext = () => {
        // Mark current step as completed
        setCompletedSteps(prev => new Set(prev).add(activeStep));

        if (activeStep < steps.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleStepClick = (stepIndex: number) => {
        // Allow clicking on completed steps or the next available step
        if (completedSteps.has(stepIndex) || stepIndex <= activeStep) {
            setActiveStep(stepIndex);
        }
    };

    // Handle form input changes
    const handleInputChange = (field: keyof ProductData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Allowed file extensions for upload
    const allowedExtensions = [
        ".pdf",
        ".doc",
        ".docx",
        ".odt",
        ".rtf",
        ".xls",
        ".xlsx",
        ".ods",
        ".ppt",
        ".pptx",
        ".odp",
        ".txt",
        ".html",
        ".htm",
        ".css",
        ".csv",
        ".tsv",
        ".js",
        ".mjs",
        ".ts",
        ".tsx",
        ".jsx",
        ".py",
        ".java",
        ".c",
        ".h",
        ".cs",
        ".php",
        ".rb",
        ".pl",
        ".sh",
        ".bash",
        ".xml",
        ".yaml",
        ".yml",
        ".md",
        ".markdown",
        ".sql",
        ".json",
        ".graphql",
        ".gql",
        ".xhtml",
        ".vue",
        ".svelte",
        ".ini",
        ".cfg",
        ".conf",
        ".env",
        ".toml",
        ".properties",
        ".log",
        ".rst",
        ".tex"
    ];

    const isFileAllowed = useCallback((file: File): boolean => {
        const fileName = file.name.toLowerCase();
        return allowedExtensions.some(ext => fileName.endsWith(ext));
    }, []);

    const { handleUpload } = useFileUpload({
        onUploadProgress: progress => lifecycleHandlersRef.current?.onUploadProgress(progress),
        onSavingFileToDatabaseStart: () => lifecycleHandlersRef.current?.onSavingFileToDatabaseStart(),
        onFileTextExtractionEvent: (_fileName: string, event: FileExtractionEvent) => {
            lifecycleHandlersRef.current?.onFileTextExtractionEvent(event);
        },
        onError: message => {
            lifecycleHandlersRef.current?.onUploadError(message);
        },
        runVectorizer: true
    });

    // Auto-set file scope to current product after upload
    const scopeUploadedFile = useCallback(
        async (uploadedFileId: number, uploadedFileName: string) => {
            try {
                const token = await getToken();
                if (!token) {
                    console.error("No auth token available");
                    return;
                }

                const scopePayload = {
                    fileid: uploadedFileId,
                    is_global: false,
                    company_id: null,
                    project_ids: [productId],
                    feature_ids: [],
                    changes: {
                        added_project_ids: [productId],
                        removed_project_ids: [],
                        added_feature_ids: [],
                        removed_feature_ids: []
                    }
                };

                const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(scopePayload)
                });

                if (!response.ok) {
                    console.error("Failed to set file scope:", response.statusText);
                    return;
                }

                console.log(`File ${uploadedFileName} scoped to product ${productId}`);
                setUploadedFileCount(prev => prev + 1);

                markWorkProductsNeedingRebuild([productId], null, null, null, true, token).catch(err => {
                    console.error("Error triggering rebuild:", err);
                });
            } catch (err) {
                console.error("Error setting file scope:", err);
            }
        },
        [getToken, productId]
    );

    const queue = useSequentialFileUploadQueue({
        handleUpload,
        onFileSucceeded: ({ fileId, file }) => {
            void scopeUploadedFile(fileId, file.name);
        },
        onFileFailed: ({ file, error }) => {
            console.error(`Upload failed for ${file.name}:`, error);
        },
        registerLifecycleHandlers: handlers => {
            lifecycleHandlersRef.current = handlers;
        }
    });

    const enqueueAllowedFiles = useCallback((files: File[]) => {
        const allowed = files.filter(isFileAllowed);
        queue.enqueueFiles(allowed);
    }, [isFileAllowed, queue.enqueueFiles]);

    // Handle file drop
    const onDrop = useCallback(
        (accepted: File[]) => {
            if (!accepted?.length) return;
            enqueueAllowedFiles(accepted);
        },
        [enqueueAllowedFiles]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        noClick: true,
        noKeyboard: true
    });

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    const handleBrowseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        manualInputRef.current?.click();
    }, []);

    const handleManualInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            enqueueAllowedFiles(files);
            e.currentTarget.value = "";
        },
        [enqueueAllowedFiles]
    );
    const uploadRows = useMemo(
        () => queue.rows.filter(row => row.stage !== "indexed"),
        [queue.rows]
    );

    // Handle final submission
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setRebuildStatus([]);
        setQuickComparePhase("");

        try {
            const token = await getToken();
            if (!token) {
                console.error("No auth token available");
                setIsSubmitting(false);
                return;
            }

            // Check if we're adding a new feature
            const isAddingNewFeature = formData.type === "NewFeature";

            if (isAddingNewFeature) {
                setRebuildStatus(prev => [...prev, "Creating new feature..."]);

                // Create the new feature
                const featureData = {
                    projectid: productId,  // Backend expects 'projectid' not 'project_id'
                    title: formData.name,
                    description: formData.description
                };

                const newFeature = await createFeatureApi(featureData, token);
                const newFeatureId = newFeature.featureid || newFeature.id;
                setRebuildStatus(prev => [...prev, "✓ Feature created successfully"]);

                // Generate user stories for the newly created feature
                try {
                    if (!userId) {
                        console.warn("User ID not available, skipping user story generation");
                        setRebuildStatus(prev => [...prev, "⚠ Warning: No user ID, skipping user story generation"]);
                    } else {
                        setRebuildStatus(prev => [...prev, "Generating user stories..."]);

                        // Call user story streaming endpoint
                        const userStoryResponse = await fetchWithAuth(`${BACKEND_URI}/api/llm/user-stories/stream`, token, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify({
                                project_id: productId,
                                feature_id: newFeatureId,
                                user_id: userId
                            })
                        });

                        if (!userStoryResponse.ok) {
                            const errorText = await userStoryResponse.text();
                            console.error("Failed to start user story generation:", userStoryResponse.status, errorText);
                            setRebuildStatus(prev => [...prev, `⚠ Warning: User story generation failed - ${errorText}`]);
                        } else {
                            // Parse SSE stream for user stories
                            const userStoryReader = userStoryResponse.body?.getReader();
                            const userStoryDecoder = new TextDecoder();

                            if (!userStoryReader) {
                                console.error("No reader available for user stories");
                                setRebuildStatus(prev => [...prev, "⚠ Warning: Unable to read user story stream"]);
                            } else {
                                let userStoryBuffer = "";
                                let userStoryCount = 0;

                                while (true) {
                                    const { done, value } = await userStoryReader.read();

                                    if (done) {
                                        console.log("User story stream complete");
                                        break;
                                    }

                                    userStoryBuffer += userStoryDecoder.decode(value, { stream: true });
                                    const lines = userStoryBuffer.split("\n\n");
                                    userStoryBuffer = lines.pop() || "";

                                    for (const line of lines) {
                                        if (line.startsWith("event: ")) {
                                            const eventType = line.substring(7, line.indexOf("\n"));
                                            const dataLine = line.substring(line.indexOf("data: ") + 6);

                                            console.log(`User Story SSE Event: ${eventType}`);

                                            try {
                                                const data = JSON.parse(dataLine);

                                                if (eventType === "status") {
                                                    setRebuildStatus(prev => [...prev, data.message]);
                                                } else if (eventType === "user_story") {
                                                    // Update UI with each user story as it's created
                                                    userStoryCount = data.story_number;
                                                    setRebuildStatus(prev => [...prev, `✓ User Story ${data.story_number}: ${data.title}`]);
                                                } else if (eventType === "complete") {
                                                    console.log("User story generation complete!", data);
                                                    setRebuildStatus(prev => [...prev, `✓ Generated ${data.total_stories} user stories`]);
                                                } else if (eventType === "error") {
                                                    setRebuildStatus(prev => [...prev, `❌ Error: ${data.error}`]);
                                                    console.error("User story generation error:", data.error);
                                                }
                                            } catch (e) {
                                                console.error("Failed to parse user story SSE data:", e);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                } catch (userStoryError) {
                    console.error("Error generating user stories:", userStoryError);
                    setRebuildStatus(prev => [
                        ...prev,
                        `⚠ Warning: ${userStoryError instanceof Error ? userStoryError.message : "Unknown error during user story generation"}`
                    ]);
                    // Don't throw - feature was created successfully, just user stories failed
                }

                // Continue to analysis flow instead of returning early
                // The new feature affects product-level work products
            }

            // For editing existing products, update project details
            // For new features, we skip this step as we only created the feature
            if (!isAddingNewFeature) {
                setRebuildStatus(prev => [...prev, "Saving product details..."]);

                // Update project name and description
                await updateProjectApi(
                    {
                        project_id: productId,
                        project_name: formData.name,
                        project_description: formData.description
                    },
                    token
                );

                // Update answers in workproduct_user_answers table
                const updatePromises: Promise<any>[] = [];

                if (answerIds.type) {
                    updatePromises.push(updateUserAnswer(answerIds.type, formData.type, token));
                }
                if (answerIds.users) {
                    updatePromises.push(updateUserAnswer(answerIds.users, formData.users, token));
                }
                if (answerIds.problem) {
                    updatePromises.push(updateUserAnswer(answerIds.problem, formData.problem, token));
                }
                if (answerIds.solution) {
                    updatePromises.push(updateUserAnswer(answerIds.solution, formData.solution, token));
                }

                // Wait for all updates to complete
                await Promise.all(updatePromises);
                setRebuildStatus(prev => [...prev, "✓ Product details saved"]);
            }

            // For new features, we'll use empty original answers to trigger full analysis
            // The quick-compare will detect this as a new feature scenario

            // Quick-compare: send original and new answers for optimized delta comparison
            if (!userId) {
                console.warn("No userId available, skipping quick-compare");
                setRebuildStatus(prev => [...prev, "⚠ Warning: No user ID, skipping comparison"]);
                if (onUpdate) await onUpdate(formData);
                setIsSubmitting(false);
                onClose();
                return;
            }

            setRebuildStatus(prev => [...prev, "Starting quick comparison..."]);

            // Build new_answers array from current formData
            const newAnswers = [
                { questionid: QUESTION_IDS.TYPE, answer: formData.type },
                { questionid: QUESTION_IDS.USERS, answer: formData.users },
                { questionid: QUESTION_IDS.PROBLEM, answer: formData.problem },
                { questionid: QUESTION_IDS.SOLUTION, answer: formData.solution }
            ];

            // For new features, use empty original answers to trigger full analysis
            const effectiveOriginalAnswers = isAddingNewFeature ? [] : originalAnswers;
            const effectiveOriginalDescription = isAddingNewFeature ? "" : originalDescription;

            const quickCompareUrl = getWorkProcessorUrl("/api/quick-compare");
            const startResponse = await fetchWithAuth(quickCompareUrl, token, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    projectid: productId,
                    userid: userId,
                    original_answers: effectiveOriginalAnswers,
                    new_answers: newAnswers,
                    original_description: effectiveOriginalDescription,
                    is_new_feature: isAddingNewFeature  // Flag to help backend understand context
                })
            });

            if (!startResponse.ok) {
                console.error("Quick compare start failed:", startResponse.status);
                setRebuildStatus(prev => [...prev, "⚠ Quick comparison failed to start"]);
                setIsSubmitting(false);
                return;
            }

            const { job_id } = await startResponse.json();
            console.log("Quick compare job started:", job_id);

            // Poll for quick-compare status
            const pollQuickCompareStatus = async (): Promise<void> => {
                try {
                    const freshToken = await getToken();
                    if (!freshToken) {
                        setRebuildStatus(prev => [...prev, "⚠ Authentication expired"]);
                        setIsSubmitting(false);
                        return;
                    }

                    const statusUrl = getWorkProcessorUrl(`/api/compare-specifications/${job_id}/status`);
                    const statusResponse = await fetchWithAuth(statusUrl, freshToken, { method: "GET" });

                    if (!statusResponse.ok) {
                        console.error("Quick compare status poll failed:", statusResponse.status);
                        setRebuildStatus(prev => [...prev, "⚠ Quick comparison failed"]);
                        setIsSubmitting(false);
                        return;
                    }

                    const status = await statusResponse.json();
                    console.log("Quick compare status:", status);

                    // Format elapsed time for display
                    const formatElapsedTime = (seconds: number): string => {
                        if (seconds < 60) return `${Math.round(seconds)}s`;
                        const mins = Math.floor(seconds / 60);
                        const secs = Math.round(seconds % 60);
                        return `${mins}m ${secs}s`;
                    };

                    // Update UI with progress
                    setQuickComparePhase(status.phase || "");
                    setRebuildStatus(prev => {
                        const filtered = prev.filter(
                            s =>
                                !s.startsWith("Step ") &&
                                !s.startsWith("Elapsed:") &&
                                !s.startsWith("Phase:")
                        );
                        const stepInfo = `Step ${status.step}/${status.total_steps}: ${status.message}`;
                        const elapsedInfo = status.elapsed_seconds
                            ? `Elapsed: ${formatElapsedTime(status.elapsed_seconds)}`
                            : "";
                        return elapsedInfo
                            ? [...filtered, stepInfo, elapsedInfo]
                            : [...filtered, stepInfo];
                    });

                    if (status.status === "COMPLETED") {
                        setRebuildStatus(prev => [...prev, "✓ Quick comparison complete"]);

                        const result = status.comparison_result;
                        setComparisonResult(result);
                        setIsSubmitting(false);

                        // Route based on quick_compare_status
                        const qcStatus = result?.quick_compare_status;

                        switch (qcStatus) {
                            case "NO_CHANGES":
                                setShowNoChangesDialog(true);
                                break;
                            case "DRASTIC_CHANGE":
                                setNewProductName(formData.name ? `${formData.name} v2` : "");
                                setShowDrasticChangeDialog(true);
                                break;
                            case "REFINEMENT_ONLY":
                                setShowRefinementOnlyDialog(true);
                                break;
                            case "COMPLETED":
                                setShowMinorEvolutionDialog(true);
                                break;
                            default:
                                // Fallback: use scope_assessment verdict for backward compat
                                if (result?.scope_assessment?.verdict === "MINOR_EVOLUTION") {
                                    setShowMinorEvolutionDialog(true);
                                } else {
                                    setNewProductName(formData.name ? `${formData.name} v2` : "");
                                    setShowDrasticChangeDialog(true);
                                }
                                break;
                        }
                        return;
                    } else if (status.status === "FAILED") {
                        // Error - auto-cancel and rollback
                        console.error("Quick compare failed:", status.error);
                        setRebuildStatus(prev => [...prev, "⚠ Analysis failed - rolling back changes..."]);

                        try {
                            const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                            await fetchWithAuth(applyUrl, freshToken, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                    projectid: productId,
                                    userid: userId,
                                    action: "CANCEL_UPDATE",
                                    comparison_result: {},
                                    original_answers: originalAnswers
                                })
                            });
                            console.log("Update cancelled and rolled back successfully");
                        } catch (cancelError) {
                            console.error("Error rolling back:", cancelError);
                        }

                        setIsSubmitting(false);
                        setShowProductTooLargeDialog(true);
                        return;
                    }

                    // Still processing, poll again in 1.5s
                    setTimeout(pollQuickCompareStatus, 1500);
                } catch (pollError) {
                    console.error("Error polling quick compare status:", pollError);
                    setRebuildStatus(prev => [...prev, "⚠ Quick comparison failed"]);
                    setIsSubmitting(false);
                }
            };

            // Start polling
            pollQuickCompareStatus();

        } catch (error) {
            console.error("Error updating product:", error);
            setRebuildStatus(prev => [...prev, `✗ Error: ${error instanceof Error ? error.message : "Unknown error"}`]);
            setIsSubmitting(false);
        }
    };

    // Render Step 1: Info/Warning
    const renderInfoStep = () => (
        <Box sx={{ py: 2 }}>
            <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                    mb: 3,
                    "& .MuiAlert-message": { width: "100%" }
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#ED6C02", mb: 1 }}>
                    Warning
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Updating product details, files, or integrations here may significantly alter your product.
                </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    This can affect:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                        Customizations you've made to the work products
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                        Product Level ({projectStats?.product_count ?? 0} potentially impacted)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                        Features Level ({projectStats?.feature_count ?? 0} potentially impacted)
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                        User Stories ({projectStats?.user_story_count ?? 0} potentially impacted)
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 3 }}>
                You will be able to review and confirm any changes to each affected Work Product directly within the editor, before those updates are applied.
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Click "NEXT" to proceed, or cancel to return to the Product Hub.
            </Typography>
        </Box>
    );

    // Render Step 2: Details Form
    const renderDetailsStep = () => {
        // For NewFeature type, show simplified Title and Description fields
        if (formData.type === "NewFeature") {
            return (
                <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Feature Details
                    </Typography>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Title
                            </Typography>
                            <Typography sx={{ color: "error.main" }}>*</Typography>
                        </Box>
                        <TextField
                            fullWidth
                            placeholder="Enter a descriptive feature title..."
                            value={formData.name}
                            onChange={e => handleInputChange("name", e.target.value)}
                            required
                            error={!formData.name.trim()}
                            helperText={!formData.name.trim() ? "Title is required" : ""}
                            size="small"
                        />
                    </Box>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Description
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth
                            placeholder="Describe what this feature will do and why it's needed..."
                            value={formData.description}
                            onChange={e => handleInputChange("description", e.target.value)}
                            multiline
                            rows={4}
                            size="small"
                        />
                    </Box>
                </Box>
            );
        }

        // For other types (NewProduct, Existing), show full product fields
        return (
            <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel id="type-label" size="small">
                            Type
                        </InputLabel>
                        <Select labelId="type-label" value={formData.type} label="Type" size="small" disabled>
                            {productTypes.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField label="Product Name" value={formData.name} onChange={e => handleInputChange("name", e.target.value)} size="small" fullWidth />
                </Box>

                <TextField
                    label="Product Description"
                    value={formData.description}
                    onChange={e => handleInputChange("description", e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe your product..."
                />

                <TextField
                    label="Users"
                    value={formData.users}
                    onChange={e => handleInputChange("users", e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Who are the target users?"
                />

                <TextField
                    label="Problem"
                    value={formData.problem}
                    onChange={e => handleInputChange("problem", e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="What problem does this solve?"
                />

                <TextField
                    label="Solution"
                    value={formData.solution}
                    onChange={e => handleInputChange("solution", e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Describe the solution..."
                />
            </Box>
        );
    };

    // Render Step 3: Files Upload
    const renderFilesStep = () => (
        <Box sx={{ py: 2 }}>
            <Card
                {...getRootProps({ "data-testid": "update-product-files-dropzone" })}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                    boxShadow: "none",
                    border: `2px dashed ${isDragOver || isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    backgroundColor: isDragOver || isDragActive ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                    transition: "all 0.2s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    }
                }}
            >
                <CardContent sx={{ textAlign: "center", py: 4 }} onClick={handleBrowseClick}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Drop a file here or click to browse
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Supports: PDF, Documents, Spreadsheets, Markdown, Code Files (Max 50MB)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Files will be automatically associated with this product
                    </Typography>

                    {/* Hidden input for dropzone */}
                    <input {...getInputProps()} />

                    {/* Dedicated hidden input for button-driven selection */}
                    <input
                        ref={manualInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.txt,.html,.htm,.css,.csv,.tsv,.js,.mjs,.ts,.tsx,.jsx,.py,.java,.c,.h,.cs,.php,.rb,.pl,.sh,.bash,.xml,.yaml,.yml,.md,.markdown,.sql,.json,.graphql,.gql,.xhtml,.vue,.svelte,.ini,.cfg,.conf,.env,.toml,.properties,.log,.rst,.tex"
                        style={{ display: "none" }}
                        onChange={handleManualInputChange}
                        data-testid="update-product-files-input"
                    />

                    <Button variant="contained" startIcon={<AttachFileIcon />} onClick={handleBrowseClick}>
                        Choose Files
                    </Button>
                </CardContent>
            </Card>

            <UploadQueueList rows={uploadRows} onRemove={queue.removeQueuedRow} onRetry={queue.retryRow} />

            {/* Uploaded file count */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
                <Typography variant="body2" color="text.secondary" data-testid="update-product-uploaded-count">
                    Uploaded Files: {uploadedFileCount}
                </Typography>
            </Box>
        </Box>
    );

    // Handle Jira connection verified
    const handleJiraVerified = (cloudId: string, domain: string, projectId: string, projectName: string) => {
        setJiraConnected(true);
        setJiraConnectionInfo({ cloudId, domain, projectId, projectName });
        handleInputChange("jiraProject", projectName);
    };

    // Handle Jira connection error
    const handleJiraError = (error: string) => {
        console.error("Jira connection error:", error);
    };

    // Handle Jira sync
    const handleJiraSync = async () => {
        if (!userId) return;

        setIsSyncing(true);
        setSyncResult(null);

        try {
            const token = await getToken();
            if (!token) {
                setSyncResult({ success: false, message: "No auth token available" });
                return;
            }

            const response = await syncAtlassianData({ userId }, token);

            if (response.status === "NOT_LINKED") {
                setSyncResult({ success: false, message: response.message || "No Atlassian integration found" });
            } else if (response.status === "NOT_ACTIVE") {
                setSyncResult({ success: false, message: response.message || "Atlassian integration is not active" });
            } else if (response.status === "LINKED") {
                if (response.success) {
                    setSyncResult({
                        success: true,
                        message: `Sync completed: ${response.deleted || 0} removed, ${response.imported || 0} imported`
                    });
                } else {
                    setSyncResult({ success: false, message: response.error || "Sync failed" });
                }
            }
        } catch (error) {
            console.error("Error syncing Jira:", error);
            setSyncResult({ success: false, message: "Failed to sync Jira data" });
        } finally {
            setIsSyncing(false);
        }
    };

    // Handle Azure DevOps sync
    const handleAzureDevOpsSync = async () => {
        if (!userId) return;

        setIsSyncing(true);
        setSyncResult(null);

        try {
            const token = await getToken();
            if (!token) {
                setSyncResult({ success: false, message: "No auth token available" });
                return;
            }

            // Call Azure DevOps sync endpoint
            const response = await fetchWithAuth("/api/v1/azure-devops/sync", token, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId, projectId: productId })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // Check if the endpoint doesn't exist (404) or server error (500)
                if (response.status === 404 || response.status === 500) {
                    setSyncResult({ success: false, message: "Azure DevOps sync is not yet available. Please check back later." });
                } else {
                    setSyncResult({ success: false, message: errorData.error || errorData.message || "Azure DevOps sync failed" });
                }
                return;
            }

            const data = await response.json();
            if (data.success) {
                setSyncResult({
                    success: true,
                    message: `Sync completed: ${data.deleted || 0} removed, ${data.imported || 0} imported`
                });
            } else {
                setSyncResult({ success: false, message: data.error || "Azure DevOps sync failed" });
            }
        } catch (error) {
            console.error("Error syncing Azure DevOps:", error);
            setSyncResult({ success: false, message: "Azure DevOps sync is not yet available. Please check back later." });
        } finally {
            setIsSyncing(false);
        }
    };

    // Render Step 4: Integration Sync
    const renderIntegrationStep = () => {
        // Render Azure DevOps integration
        const renderAzureDevOpsIntegration = () => (
            <Box
                sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    p: 2
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box
                        component="img"
                        src="https://cdn.vsassets.io/content/icons/favicon.ico"
                        alt="Azure DevOps"
                        sx={{ width: 24, height: 24 }}
                        onError={e => {
                            (e.target as HTMLImageElement).style.display = "none";
                        }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Azure DevOps
                    </Typography>
                </Box>

                {azureDevOpsConnected && azureDevOpsConnectionInfo ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CheckCircleIcon sx={{ color: "success.main" }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Connected to: {azureDevOpsConnectionInfo.projectName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {azureDevOpsConnectionInfo.organization}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SyncIcon />}
                                onClick={handleAzureDevOpsSync}
                                disabled={isSyncing}
                                sx={{ ml: "auto" }}
                            >
                                Synchronize
                            </Button>
                        </Box>
                        {syncResult && (
                            <Alert severity={syncResult.success ? "success" : "error"} onClose={() => setSyncResult(null)} sx={{ mt: 1 }}>
                                {syncResult.message}
                            </Alert>
                        )}
                    </Box>
                ) : isLoadingAzureDevOpsInfo ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Loading Azure DevOps integration...
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Azure DevOps integration is linked to this product. Configure Azure DevOps connection in Integrations settings.
                        </Typography>
                    </Box>
                )}
            </Box>
        );

        // Render Jira integration
        const renderJiraIntegration = () => (
            <Box
                sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    p: 2
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box
                        component="img"
                        src="https://cdn.worldvectorlogo.com/logos/jira-1.svg"
                        alt="Jira"
                        sx={{ width: 24, height: 24 }}
                        onError={e => {
                            (e.target as HTMLImageElement).style.display = "none";
                        }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Jira
                    </Typography>
                </Box>

                {jiraConnected && jiraConnectionInfo ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CheckCircleIcon sx={{ color: "success.main" }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Connected to: {jiraConnectionInfo.projectName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {jiraConnectionInfo.domain}
                                </Typography>
                            </Box>
                            <Button variant="outlined" size="small" startIcon={<SyncIcon />} onClick={handleJiraSync} disabled={isSyncing} sx={{ ml: "auto" }}>
                                Synchronize
                            </Button>
                        </Box>
                        {syncResult && (
                            <Alert severity={syncResult.success ? "success" : "error"} onClose={() => setSyncResult(null)} sx={{ mt: 1 }}>
                                {syncResult.message}
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <AtlassianConnector
                        initialProjectId={formData.jiraProject}
                        hideCancelButton={true}
                        onVerified={handleJiraVerified}
                        onError={handleJiraError}
                        onCancel={() => {}}
                        backendUrl={AGENT_URL}
                        userId={userId}
                    />
                )}
            </Box>
        );

        return (
            <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Render integration based on pmToolType */}
                {pmToolType === "AZURE_DEVOPS" ? renderAzureDevOpsIntegration() : renderJiraIntegration()}

                {/* GitHub Integration - Hidden for now, not yet implemented */}
            </Box>
        );
    };

    // Render Step 5: Change Impact
    const renderImpactStep = () => {
        // Use projectStats as the source of truth for counts (same data as Info step)
        // Fall back to featureHierarchy counts if projectStats is unavailable
        const featuresChanged = projectStats?.feature_count ?? featureHierarchy?.features?.length ?? 0;
        const workProductsOutOfDate = projectStats?.user_story_count ?? featureHierarchy?.features?.reduce((total, feature) => total + (feature.user_stories?.length ?? 0), 0) ?? 0;
        const timestamp = new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
        const impactedFeatures = featureHierarchy?.features?.map(f => f.title) ?? [];
        const impactedUserStories = featureHierarchy?.features?.flatMap(f => f.user_stories?.map(s => s.story_number) ?? []) ?? [];

        // Use changeImpact prop if provided, otherwise use calculated values
        const impact = changeImpact || {
            featuresChanged,
            workProductsOutOfDate,
            timestamp,
            impactedFeatures,
            impactedUserStories
        };

        return (
            <Box sx={{ py: 2 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: "#ED6C02",
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Information you have modified will mark the following work products as needing to be manually updated at your discretion:<br></br>1 Product
                    Changed → {impact.featuresChanged} {impact.featuresChanged > 1 ? "Features Changed" : "Feature Changed"} → {impact.workProductsOutOfDate}{" "}
                    {impact.workProductsOutOfDate > 1 ? "User Stories" : "User Story"} Changed
                </Typography>

                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Impacted Items: {detailedStats?.projectname || formData.name || ""}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "120px 1fr",
                            gap: 1,
                            alignItems: "start"
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Product:
                        </Typography>
                        <Typography variant="body2" sx={{ wordWrap: "break-word" }}>
                            {detailedStats?.workproduct_list || "None"}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Features:
                        </Typography>
                        <Typography variant="body2" sx={{ wordWrap: "break-word" }}>
                            {detailedStats?.features_list || "None"}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            User Stories:
                        </Typography>
                        <Typography variant="body2" sx={{ wordWrap: "break-word" }}>
                            {detailedStats?.user_story_list || "None"}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    // Render current step content based on step key (not index, since steps can be filtered)
    const renderStepContent = () => {
        const currentStepKey = steps[activeStep]?.key;
        switch (currentStepKey) {
            case "info":
                return renderInfoStep();
            case "details":
                return renderDetailsStep();
            case "files":
                return renderFilesStep();
            case "integration":
                return renderIntegrationStep();
            case "impact":
                return renderImpactStep();
            default:
                return null;
        }
    };

    // Check if we can proceed to next step based on step key
    const canProceed = () => {
        const currentStepKey = steps[activeStep]?.key;
        switch (currentStepKey) {
            case "info": // Info step - always can proceed
                return true;
            case "details": // Details step - need at least name
                return formData.name.trim().length > 0;
            case "files": // Files step - optional, always can proceed
                return true;
            case "integration": // Integration step - optional, always can proceed
                return true;
            case "impact": // Impact step - final step
                return true;
            default:
                return true;
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={(_, reason) => {
                    // Prevent closing on backdrop click - user must use Cancel button or X
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                maxWidth={false}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 2,
                        maxHeight: isMobile ? "100vh" : "90vh",
                        maxWidth: isMobile ? "100%" : 750
                    }
                }}
            >
                {/* Loading overlay for initial data fetch */}
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: theme => theme.zIndex.modal + 1,
                        position: "absolute",
                        borderRadius: isMobile ? 0 : 2
                    }}
                    open={isLoadingData}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress color="inherit" />
                        <Typography variant="body1" color="inherit">
                            Loading product data...
                        </Typography>
                    </Box>
                </Backdrop>

                {/* Loading overlay for sync operations */}
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: theme => theme.zIndex.modal + 1,
                        position: "absolute",
                        borderRadius: isMobile ? 0 : 2
                    }}
                    open={isSyncing}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress color="inherit" />
                        <Typography variant="body1" color="inherit">
                            Synchronizing {pmToolType === "AZURE_DEVOPS" ? "Azure DevOps" : "Jira"} data...
                        </Typography>
                    </Box>
                </Backdrop>

                {/* Rebuild progress overlay */}
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: theme => theme.zIndex.modal + 1,
                        position: "absolute",
                        borderRadius: isMobile ? 0 : 2,
                        backgroundColor: "rgba(0, 0, 0, 0.85)"
                    }}
                    open={isSubmitting}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            maxWidth: 500,
                            width: "90%",
                            p: 3
                        }}
                    >
                        <Typography variant="h6" color="inherit" sx={{ mb: 1 }}>
                            {quickComparePhase ? "Analyzing Changes" : "Saving Changes"}
                        </Typography>

                        <CircularProgress color="inherit" size={40} />
                        {quickComparePhase && (
                            <Typography variant="caption" color="inherit" sx={{ mt: 0.5 }}>
                                Phase: {quickComparePhase.replace(/_/g, " ")}
                            </Typography>
                        )}

                        {/* Status messages */}
                        <Box
                            sx={{
                                width: "100%",
                                maxHeight: 200,
                                overflowY: "auto",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                borderRadius: 1,
                                p: 1.5
                            }}
                        >
                            {rebuildStatus.map((status, index) => (
                                <Typography
                                    key={index}
                                    variant="body2"
                                    color="inherit"
                                    sx={{
                                        py: 0.25,
                                        fontSize: "0.85rem",
                                        opacity: index === rebuildStatus.length - 1 ? 1 : 0.7
                                    }}
                                >
                                    {status}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Backdrop>

                {/* Saving progress overlay */}
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: theme => theme.zIndex.modal + 1,
                        position: "absolute",
                        borderRadius: isMobile ? 0 : 2,
                        backgroundColor: "rgba(0, 0, 0, 0.85)"
                    }}
                    open={isSaving}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress color="inherit" size={40} />
                        <Typography variant="h6" color="inherit">
                            Saving Product Updates...
                        </Typography>
                        <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
                            Please wait while your changes are being applied
                        </Typography>
                    </Box>
                </Backdrop>

                {/* Build product progress overlay */}
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: theme => theme.zIndex.modal + 1,
                        position: "absolute",
                        borderRadius: isMobile ? 0 : 2,
                        backgroundColor: "rgba(0, 0, 0, 0.9)"
                    }}
                    open={isBuildingProduct}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            maxWidth: 500,
                            width: "90%",
                            p: 3
                        }}
                    >
                        <Typography variant="h6" color="inherit" sx={{ mb: 1 }}>
                            {buildJobError ? "Build Failed" : buildJobProgress >= 100 ? "Build Complete!" : "Building New Product"}
                        </Typography>

                        {!buildJobError && (
                            <>
                                <Box sx={{ width: "100%", mb: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={buildJobProgress}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 4,
                                                backgroundColor: buildJobProgress >= 100 ? "#4caf50" : "#90caf9"
                                            }
                                        }}
                                    />
                                </Box>

                                <Typography variant="body2" color="inherit" sx={{ opacity: 0.9, textAlign: "center" }}>
                                    {buildJobMessage}
                                </Typography>

                                {buildJobProgress < 100 && (
                                    <CircularProgress color="inherit" size={24} sx={{ mt: 1 }} />
                                )}
                                {buildJobProgress >= 100 && (
                                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 40, mt: 1 }} />
                                )}
                            </>
                        )}

                        {buildJobError && (
                            <>
                                <WarningIcon sx={{ color: "#ff9800", fontSize: 40 }} />
                                <Typography variant="body2" color="inherit" sx={{ textAlign: "center", opacity: 0.9 }}>
                                    {buildJobError}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", mt: 1 }}
                                    onClick={() => {
                                        setIsBuildingProduct(false);
                                        setBuildJobError(null);
                                        onClose();
                                    }}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                    </Box>
                </Backdrop>

                {/* Header */}
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 2,
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider"
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Edit Product
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* Stepper */}
                <Box sx={{ px: 3, pt: 2, pb: 1, bgcolor: "#FAFBFC" }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((step, index) => (
                            <Step
                                key={step.key}
                                completed={completedSteps.has(index)}
                                onClick={() => handleStepClick(index)}
                                sx={{ cursor: completedSteps.has(index) || index <= activeStep ? "pointer" : "default" }}
                            >
                                <StepLabel
                                    StepIconComponent={() => (
                                        <StepIcon active={activeStep === index} completed={completedSteps.has(index)} stepNumber={index + 1} />
                                    )}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: activeStep === index ? "#103699" : "text.secondary",
                                            fontWeight: activeStep === index ? 600 : 400
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Content */}
                <DialogContent sx={{ px: 3, height: 508, overflow: "auto" }}>{renderStepContent()}</DialogContent>

                {/* Footer */}
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "#F1F5FE"
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {activeStep === 4 ? "Scroll to the bottom to action these changes" : "Click next or use the menu above to navigate"}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    borderColor: "#D0D7E2",
                                    color: "text.secondary",
                                    "&:hover": {
                                        borderColor: "#B0B7C2",
                                        bgcolor: "transparent"
                                    }
                                }}
                            >
                                CANCEL
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                sx={{
                                    borderColor: "#103699",
                                    color: "#103699",
                                    "&:hover": {
                                        borderColor: "#0d2d7a",
                                        bgcolor: "rgba(16, 54, 153, 0.04)"
                                    },
                                    "&:disabled": {
                                        borderColor: "#D0D7E2",
                                        color: "text.disabled"
                                    }
                                }}
                            >
                                BACK
                            </Button>

                            {activeStep === steps.length - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || !canProceed()}
                                    sx={{
                                        bgcolor: "#103699",
                                        "&:hover": { bgcolor: "#0d2d7a" }
                                    }}
                                >
                                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "SUBMIT"}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={!canProceed()}
                                    endIcon={<span>›</span>}
                                    sx={{
                                        bgcolor: "#103699",
                                        "&:hover": { bgcolor: "#0d2d7a" }
                                    }}
                                >
                                    NEXT
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Dialog>

            {/* Drastic Scope Change Dialog - shown when verdict is NOT MINOR_EVOLUTION */}
            <Dialog
                open={showDrasticChangeDialog}
                onClose={(_, reason) => {
                    // Prevent closing on backdrop click or escape - user must use buttons
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setShowDrasticChangeDialog(false);
                    }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "#d32f2f" }}>
                    <WarningIcon color="error" />
                    Warning!
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                        Your recent changes require creating a new product, as they can`t be applied to the current one. If you proceed, the existing product
                        will remain unchanged and a new one will be generated.
                    </Typography>
                    <TextField
                        label="New Product Name"
                        value={newProductName}
                        onChange={e => setNewProductName(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Would you like to continue?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={async () => {
                            setShowDrasticChangeDialog(false);
                            // Call API to cancel the update and rollback to original answers
                            try {
                                const token = await getToken();
                                if (token) {
                                    const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                    const response = await fetchWithAuth(applyUrl, token, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            projectid: productId,
                                            userid: userId,
                                            action: "CANCEL_UPDATE",
                                            comparison_result: {},
                                            original_answers: originalAnswers
                                        })
                                    });
                                    if (response.ok) {
                                        console.log("Update cancelled successfully");
                                    } else {
                                        console.error("Failed to cancel update:", response.status);
                                    }
                                }
                            } catch (error) {
                                console.error("Error cancelling update:", error);
                            }
                            onClose();
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            setShowDrasticChangeDialog(false);
                            setIsSaving(true);
                            // Call API to create a new product with the comparison result
                            try {
                                const token = await getToken();
                                if (token) {
                                    const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                    const response = await fetchWithAuth(applyUrl, token, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            projectid: productId,
                                            userid: userId,
                                            action: "CREATE_NEW_PRODUCT",
                                            new_project_name: newProductName.trim() || formData.name || "New Product",
                                            comparison_result: comparisonResult,
                                            original_answers: originalAnswers
                                        })
                                    });
                                    if (response.ok) {
                                        const applyData = await response.json();
                                        const result = applyData.result;
                                        console.log("New product creation response:", applyData);

                                        // New flow: backend returns requires_job_run with run_job_params
                                        if (result?.requires_job_run && result?.run_job_params) {
                                            const newProjectId = result.new_project_id;
                                            setBuildJobNewProjectId(newProjectId);
                                            setIsSaving(false);
                                            setIsBuildingProduct(true);
                                            setBuildJobProgress(0);
                                            setBuildJobMessage("Setting up new product...");
                                            setBuildJobError(null);

                                            // Update the new project's name via the main backend API
                                            const productName = newProductName.trim() || formData.name || "New Product";
                                            try {
                                                await updateProjectApi(
                                                    {
                                                        project_id: newProjectId,
                                                        project_name: productName,
                                                        project_description: productName,
                                                        status: "Active"
                                                    },
                                                    token
                                                );
                                                console.log("New project name set to:", productName);
                                            } catch (nameError) {
                                                console.error("Failed to set new project name:", nameError);
                                                // Non-fatal - continue with the build
                                            }

                                            setBuildJobMessage("Starting full product build...");

                                            // Call /api/run-job with the params from the apply response
                                            const runJobUrl = getWorkProcessorUrl("/api/run-job");
                                            const runJobResponse = await fetchWithAuth(runJobUrl, token, {
                                                method: "POST",
                                                headers: { "Content-Type": "application/json" },
                                                body: JSON.stringify(result.run_job_params)
                                            });

                                            if (runJobResponse.ok) {
                                                const runJobData = await runJobResponse.json();
                                                const jobId = runJobData.job_id;
                                                console.log("Build job started:", jobId);

                                                // Start polling for job status
                                                const pollBuildJobStatus = async () => {
                                                    try {
                                                        const freshToken = await getToken();
                                                        if (!freshToken) {
                                                            setBuildJobError("Authentication expired");
                                                            setIsBuildingProduct(false);
                                                            return;
                                                        }

                                                        const statusUrl = getWorkProcessorUrl(`/api/job/${jobId}/status`);
                                                        const statusResponse = await fetchWithAuth(statusUrl, freshToken, { method: "GET" });

                                                        if (!statusResponse.ok) {
                                                            console.error("Build job status poll failed:", statusResponse.status);
                                                            setBuildJobError("Failed to check build status");
                                                            setIsBuildingProduct(false);
                                                            return;
                                                        }

                                                        const statusData = await statusResponse.json();
                                                        console.log("Build job status:", statusData);

                                                        // Update progress UI
                                                        setBuildJobProgress(statusData.progress || 0);
                                                        setBuildJobMessage(statusData.message || "Building product...");
                                                        setBuildJobSectionsCompleted(statusData.sections_completed || 0);
                                                        setBuildJobTotalSections(statusData.total_sections || 0);

                                                        if (statusData.status === "COMPLETED") {
                                                            // Build finished - now import features from pre-processed data
                                                            setBuildJobProgress(90);
                                                            setBuildJobMessage("Importing features and user stories...");

                                                            try {
                                                                const featureToken = await getToken();
                                                                if (featureToken && newProjectId) {
                                                                    const FEATURES_SECTION_ID = [232, 238, 237];
                                                                    const featureResponse = await fetchWithAuth(
                                                                        `${BACKEND_URI}/api/features/from-pre-processing`,
                                                                        featureToken,
                                                                        {
                                                                            method: "POST",
                                                                            headers: { "Content-Type": "application/json" },
                                                                            body: JSON.stringify({
                                                                                projectid: newProjectId,
                                                                                sectionid: FEATURES_SECTION_ID
                                                                            })
                                                                        }
                                                                    );

                                                                    if (featureResponse.ok) {
                                                                        const featureResult = await featureResponse.json();
                                                                        console.log("Feature import result:", featureResult);
                                                                        const featureCount = featureResult.features?.length || 0;
                                                                        setBuildJobMessage(`Imported ${featureCount} features with user stories!`);
                                                                    } else {
                                                                        console.error("Feature import failed:", featureResponse.status);
                                                                        setBuildJobMessage("Product built (feature import had issues)");
                                                                    }
                                                                }
                                                            } catch (featureError) {
                                                                console.error("Error importing features:", featureError);
                                                                setBuildJobMessage("Product built (feature import had issues)");
                                                            }

                                                            setBuildJobProgress(100);
                                                            setBuildJobMessage("Product built successfully!");
                                                            // Refresh the project list
                                                            if (onUpdate) {
                                                                await onUpdate(formData);
                                                            }
                                                            // Brief delay to show completion, then close
                                                            setTimeout(() => {
                                                                setIsBuildingProduct(false);
                                                                onClose();
                                                            }, 1500);
                                                        } else if (statusData.status === "FAILED") {
                                                            // Build failed
                                                            setBuildJobError(statusData.message || statusData.error || "Build failed");
                                                        } else {
                                                            // Still processing (QUEUED or PROCESSING), poll again
                                                            buildJobPollingRef.current = setTimeout(pollBuildJobStatus, 2000);
                                                        }
                                                    } catch (pollError) {
                                                        console.error("Error polling build job status:", pollError);
                                                        setBuildJobError("Error checking build status");
                                                    }
                                                };

                                                // Start polling
                                                buildJobPollingRef.current = setTimeout(pollBuildJobStatus, 2000);
                                            } else {
                                                console.error("Failed to start build job:", runJobResponse.status);
                                                setBuildJobError("Failed to start product build");
                                                setIsBuildingProduct(false);
                                            }
                                        } else {
                                            // Legacy flow: no job run required
                                            console.log("New product creation completed (legacy flow)");
                                            if (onUpdate) {
                                                await onUpdate(formData);
                                            }
                                            setIsSaving(false);
                                            onClose();
                                        }
                                        return; // Don't fall through to finally/onClose below
                                    } else {
                                        console.error("Failed to create new product:", response.status);
                                    }
                                }
                            } catch (error) {
                                console.error("Error creating new product:", error);
                            }
                            setIsSaving(false);
                            onClose();
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Minor Evolution Dialog - shown when verdict IS MINOR_EVOLUTION */}
            <Dialog
                open={showMinorEvolutionDialog}
                onClose={(_, reason) => {
                    // Prevent closing on backdrop click or escape - user must use buttons
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setShowMinorEvolutionDialog(false);
                        onClose();
                    }
                }}
                disableEscapeKeyDown
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column"
                    }
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "#2e7d32", flexShrink: 0 }}>
                    <CheckCircleIcon color="success" />
                    Modifications Allowed Within Product Scope
                </DialogTitle>
                <DialogContent sx={{ overflowY: "auto", flex: 1 }}>
                    {/* Added Personas */}
                    {comparisonResult?.merged_personas?.added_personas?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Personas Added:
                            </Typography>
                            <List dense>
                                {comparisonResult.merged_personas.added_personas.map((persona: any, index: number) => (
                                    <ListItem key={index} sx={{ py: 0.5 }}>
                                        <ListItemText primary={persona.name} secondary={persona.added_reason} primaryTypographyProps={{ fontWeight: 500 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {/* Added Features - only where source = "ADDED" */}
                    {comparisonResult?.merged_features?.features?.filter((f: any) => f.source === "ADDED")?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Features Added:
                            </Typography>
                            <List dense>
                                {comparisonResult.merged_features.features
                                    .filter((feature: any) => feature.source === "ADDED")
                                    .map((feature: any, index: number) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                            <ListItemText primary={feature.title} />
                                        </ListItem>
                                    ))}
                            </List>
                        </Box>
                    )}

                    {/* Added User Stories - only where source = "ADDED" */}
                    {comparisonResult?.merged_features?.features?.some((f: any) => f.user_stories?.some((s: any) => s.source === "ADDED")) && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                User Stories Added:
                            </Typography>
                            <List dense>
                                {comparisonResult.merged_features.features.map((feature: any) =>
                                    feature.user_stories
                                        ?.filter((story: any) => story.source === "ADDED")
                                        .map((story: any, storyIndex: number) => (
                                            <ListItem key={`${feature.title}-${storyIndex}`} sx={{ py: 0.5 }}>
                                                <ListItemText primary={`${feature.title} - ${story.name}`} primaryTypographyProps={{ variant: "body2" }} />
                                            </ListItem>
                                        ))
                                )}
                            </List>
                        </Box>
                    )}

                    {/* Show message if nothing was added */}
                    {!comparisonResult?.merged_personas?.added_personas?.length &&
                        !comparisonResult?.merged_features?.features?.filter((f: any) => f.source === "ADDED")?.length &&
                        !comparisonResult?.merged_features?.features?.some((f: any) => f.user_stories?.some((s: any) => s.source === "ADDED")) && (
                            <Typography variant="body1" color="text.secondary">
                                No new personas, features, or user stories were added in this update.
                            </Typography>
                        )}
                </DialogContent>
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        bgcolor: "#F5F5F5",
                        borderTop: "1px solid #E0E0E0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Do you wish to continue with the product updates?
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                setShowMinorEvolutionDialog(false);
                                // Call API to cancel the update and rollback to original answers
                                try {
                                    const token = await getToken();
                                    if (token) {
                                        const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                        const response = await fetchWithAuth(applyUrl, token, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                projectid: productId,
                                                userid: userId,
                                                action: "CANCEL_UPDATE",
                                                comparison_result: {},
                                                original_answers: originalAnswers
                                            })
                                        });
                                        if (response.ok) {
                                            console.log("Update cancelled successfully");
                                        } else {
                                            console.error("Failed to cancel update:", response.status);
                                        }
                                    }
                                } catch (error) {
                                    console.error("Error cancelling update:", error);
                                }
                                onClose();
                            }}
                        >
                            No
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                                setShowMinorEvolutionDialog(false);
                                setIsSaving(true);
                                // Call API to update the product with the comparison result
                                try {
                                    const token = await getToken();
                                    if (token) {
                                        const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                        const response = await fetchWithAuth(applyUrl, token, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                projectid: productId,
                                                userid: userId,
                                                action: "UPDATE_PRODUCT",
                                                comparison_result: comparisonResult,
                                                original_answers: originalAnswers
                                            })
                                        });
                                        if (response.ok) {
                                            console.log("Product update initiated successfully");
                                            // Check if there's actual work to be done (added personas, features, or user stories)
                                            const hasAddedPersonas = comparisonResult?.merged_personas?.added_personas?.length > 0;
                                            const hasAddedFeatures =
                                                comparisonResult?.merged_features?.features?.filter((f: any) => f.source === "ADDED")?.length > 0;
                                            const hasAddedUserStories = comparisonResult?.merged_features?.features?.some((f: any) =>
                                                f.user_stories?.some((s: any) => s.source === "ADDED")
                                            );

                                            // Only mark work products for rebuild if there's actual work to be done
                                            if (hasAddedPersonas || hasAddedFeatures || hasAddedUserStories) {
                                                await markWorkProductsNeedingRebuild([productId], null, null, null, true, token);
                                                console.log("Work products marked for rebuild");
                                            } else {
                                                console.log("No new personas, features, or user stories - skipping rebuild");
                                            }
                                            // Call optional onUpdate callback to refresh the product studio
                                            if (onUpdate) {
                                                await onUpdate(formData);
                                            }
                                        } else {
                                            console.error("Failed to update product:", response.status);
                                        }
                                    }
                                } catch (error) {
                                    console.error("Error updating product:", error);
                                } finally {
                                    setIsSaving(false);
                                }
                                onClose();
                            }}
                        >
                            Yes
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Product Too Large Dialog - shown when comparison fails due to context limits */}
            <Dialog
                open={showProductTooLargeDialog}
                onClose={(_, reason) => {
                    // Prevent closing on backdrop click or escape - user must use buttons
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setShowProductTooLargeDialog(false);
                        onClose();
                    }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "#d32f2f" }}>
                    <WarningIcon color="error" />
                    Product Too Large to Analyze
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        The product specifications are too large for the system to analyze correctly. Your changes have been rolled back to preserve the
                        original product.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>What you can do:</strong>
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                            Create a new product instead of modifying this one
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                            Break down your product into smaller, focused products
                        </Typography>
                        <Typography component="li" variant="body2">
                            Contact support if you need assistance
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setShowProductTooLargeDialog(false);
                            onClose();
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* No Changes Dialog - shown when quick_compare_status is NO_CHANGES */}
            <Dialog
                open={showNoChangesDialog}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setShowNoChangesDialog(false);
                        onClose();
                    }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "#2e7d32" }}>
                    <CheckCircleIcon color="success" />
                    No Changes Detected
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {comparisonResult?.summary || "No answer changes were detected. Your specifications remain unchanged."}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            setShowNoChangesDialog(false);
                            try {
                                const token = await getToken();
                                if (token) {
                                    const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                    await fetchWithAuth(applyUrl, token, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            projectid: productId,
                                            userid: userId,
                                            action: "CANCEL_UPDATE",
                                            comparison_result: {},
                                            original_answers: originalAnswers
                                        })
                                    });
                                }
                            } catch (error) {
                                console.error("Error rolling back:", error);
                            }
                            onClose();
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refinement Only Dialog - shown when quick_compare_status is REFINEMENT_ONLY */}
            <Dialog
                open={showRefinementOnlyDialog}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setShowRefinementOnlyDialog(false);
                        onClose();
                    }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "#2e7d32" }}>
                    <CheckCircleIcon color="success" />
                    Refinements Applied
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {comparisonResult?.gate_reasoning ||
                            "Your changes are refinements to existing features. No new features or user stories are needed."}
                    </Typography>

                    {comparisonResult?.merged_personas?.added_personas?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Personas Added:
                            </Typography>
                            <List dense>
                                {comparisonResult.merged_personas.added_personas.map(
                                    (persona: any, index: number) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary={persona.name || persona}
                                                secondary={persona.added_reason}
                                                primaryTypographyProps={{ fontWeight: 500 }}
                                            />
                                        </ListItem>
                                    )
                                )}
                            </List>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            setShowRefinementOnlyDialog(false);
                            setIsSaving(true);
                            try {
                                const token = await getToken();
                                if (token) {
                                    const hasAddedPersonas =
                                        comparisonResult?.merged_personas?.added_personas?.length > 0;

                                    if (hasAddedPersonas) {
                                        const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
                                        await fetchWithAuth(applyUrl, token, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                projectid: productId,
                                                userid: userId,
                                                action: "UPDATE_PRODUCT",
                                                comparison_result: comparisonResult,
                                                original_answers: originalAnswers
                                            })
                                        });
                                    }
                                    if (onUpdate) await onUpdate(formData);
                                }
                            } catch (error) {
                                console.error("Error applying refinements:", error);
                            } finally {
                                setIsSaving(false);
                            }
                            onClose();
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdateProductModal;
